import React from 'react';
import { useConfiguration } from '../../context/configurationContext';
import { useSkeepersAvisVerifiesListingPlacement } from '../../context/skeepersAvisVerifiesListingContext';
import {
  SkeepersAvisVerifiesWidgetMount,
  useAvisVerifiesSkeepersScript,
} from '../../components/SkeepersAvisVerifiesWidget/SkeepersAvisVerifiesWidget';
// Avoid components barrel import to reduce risk of circular dependencies in SSR chunks.
import LinkedLogo from '../../components/Logo/LinkedLogo';
import NewsletterSignup from '../../components/NewsletterSignup/NewsletterSignup';
import IconSocialMediaLinkedIn from '../../components/IconSocialMediaLinkedIn/IconSocialMediaLinkedIn';
import IconSocialMediaInstagram from '../../components/IconSocialMediaInstagram/IconSocialMediaInstagram';
import IconSocialMediaFacebook from '../../components/IconSocialMediaFacebook/IconSocialMediaFacebook';
import css from './FooterContainer.module.css';

const DiscordIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className={className}>
    <path d="M20.32 4.37a19.8 19.8 0 0 0-4.95-1.53.07.07 0 0 0-.07.03c-.21.38-.44.87-.61 1.26a18.4 18.4 0 0 0-5.38 0 12.6 12.6 0 0 0-.62-1.26.08.08 0 0 0-.07-.03 19.74 19.74 0 0 0-4.95 1.53.07.07 0 0 0-.03.03C.53 9.05-.32 13.59.1 18.08a.08.08 0 0 0 .03.06 19.9 19.9 0 0 0 6.08 3.08.08.08 0 0 0 .09-.03c.47-.65.89-1.34 1.25-2.07a.08.08 0 0 0-.04-.11 13.03 13.03 0 0 1-1.9-.9.08.08 0 0 1-.01-.13c.13-.1.26-.2.38-.31a.07.07 0 0 1 .08-.01c3.99 1.82 8.3 1.82 12.25 0a.07.07 0 0 1 .08.01c.12.11.25.21.38.31a.08.08 0 0 1-.01.13c-.6.36-1.24.66-1.9.9a.08.08 0 0 0-.04.11c.37.73.79 1.42 1.25 2.07a.08.08 0 0 0 .09.03 19.86 19.86 0 0 0 6.08-3.08.08.08 0 0 0 .03-.06c.5-5.2-.84-9.71-3.53-13.68a.06.06 0 0 0-.03-.03ZM8.02 15.35c-1.2 0-2.18-1.11-2.18-2.47 0-1.37.97-2.48 2.18-2.48 1.22 0 2.2 1.12 2.18 2.48 0 1.36-.97 2.47-2.18 2.47Zm7.97 0c-1.2 0-2.18-1.11-2.18-2.47 0-1.37.97-2.48 2.18-2.48 1.22 0 2.2 1.12 2.18 2.48 0 1.36-.97 2.47-2.18 2.47Z" />
  </svg>
);

const RedditIcon = ({ className }) => (
  <svg viewBox="0 -1.5 20 20" fill="currentColor" aria-hidden="true" className={className}>
    <path d="M13.029 11.24746C12.267 11.24746 11.628 10.6217 11.628 9.8499C11.628 9.07708 12.267 8.43103 13.029 8.43103C13.79 8.43103 14.407 9.07708 14.407 9.8499C14.407 10.6217 13.791 11.24746 13.029 11.24746M13.223 13.82961C12.55 14.51116 11.495 14.8428 9.999 14.8428C8.502 14.8428 7.448 14.51116 6.776 13.82961C6.63 13.68154 6.63 13.44219 6.776 13.2931C6.921 13.14503 7.158 13.14503 7.304 13.2931C7.829 13.82556 8.71 14.08519 9.999 14.08519C11.287 14.08519 12.169 13.82556 12.695 13.2931C12.84 13.14503 13.077 13.14503 13.223 13.2931C13.369 13.44219 13.369 13.68154 13.223 13.82961M5.592 9.8499C5.592 9.07809 6.23 8.43103 6.991 8.43103C7.752 8.43103 8.369 9.07809 8.369 9.8499C8.369 10.6217 7.752 11.24746 6.991 11.24746C6.23 11.24746 5.592 10.6217 5.592 9.8499M20 8.31339C20 7.04665 18.984 6.01623 17.735 6.01623C17.159 6.01623 16.616 6.23428 16.2 6.62475C14.705 5.63793 12.703 5 10.486 4.91278L11.709 1.12677L14.921 1.89351C14.922 2.93611 15.758 3.78296 16.786 3.78296C17.814 3.78296 18.651 2.93408 18.651 1.89148C18.651 0.84888 17.814 0 16.786 0C16.016 0 15.355 0.47465 15.07 1.15112C14.378 0.9858 11.904 0.39452 11.212 0.22921L9.702 4.90467C7.401 4.94828 5.316 5.58316 3.765 6.59331C3.354 6.22312 2.822 6.01623 2.264 6.01623C1.016 6.01623 0 7.04665 0 8.31339C0 9.11765 0.414 9.85497 1.076 10.26876C0.473 13.88134 4.67 17 9.958 17C15.224 17 19.407 13.90872 18.849 10.3144C19.557 9.91176 20 9.1572 20 8.31339" />
  </svg>
);

const FooterComponent = () => {
  const { topbar, marketplaceName } = useConfiguration();
  const { mountWidgetInListingAfterSales } = useSkeepersAvisVerifiesListingPlacement();

  useAvisVerifiesSkeepersScript();

  const handleOpenCookiePreferences = () => {
    // Déclencher un événement personnalisé pour ouvrir les préférences de cookies
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('openCookiePreferences'));
    }
  };

  return (
    <footer className={css.root}>
      <div className={css.footer}>
        <div className={css.content}>
          {/* Logo and Company Info - First section */}
          <div className={css.logoSection}>
            {/* Logo Column */}
            <div className={css.logoColumn}>
              <div className={css.logoAndReviews}>
                <LinkedLogo
                  rootClassName={css.logoLink}
                  logoClassName={css.logoWrapper}
                  logoImageClassName={css.logoImage}
                  linkToExternalSite={topbar?.logoLink}
                  layout="desktop"
                />
                {!mountWidgetInListingAfterSales ? (
                  <SkeepersAvisVerifiesWidgetMount
                    className={css.avisVerifiesWidget}
                    ariaLabel="Avis clients vérifiés"
                  />
                ) : null}
              </div>
            </div>

            {/* Social Media Column */}
            <div className={css.socialColumn}>
              <div className={css.socialLinks}>
                <h4 className={css.socialTitle}>Suivez‑nous</h4>
                <div className={css.socialIcons}>
                  <a href="https://www.linkedin.com/company/neokasa/" className={css.socialLink} aria-label="LinkedIn" target="_blank" rel="noopener noreferrer">
                    <IconSocialMediaLinkedIn className={css.socialIcon} />
                  </a>
                  <a href="https://www.facebook.com/neokasafrance/" className={css.socialLink} aria-label="Facebook" target="_blank" rel="noopener noreferrer">
                    <IconSocialMediaFacebook className={css.socialIcon} />
                  </a>
                  <a href="https://www.instagram.com/neokasa_fr/" className={css.socialLink} aria-label="Instagram" target="_blank" rel="noopener noreferrer">
                    <IconSocialMediaInstagram className={css.socialIcon} />
                  </a>
                  <a href="https://discord.gg/tEtr8tBs" className={css.socialLink} aria-label="Discord" target="_blank" rel="noopener noreferrer">
                    <DiscordIcon className={css.socialIcon} />
                  </a>
                  <a href="https://www.reddit.com/user/neokasa/" className={`${css.socialLink} ${css.redditSocialLink}`} aria-label="Reddit" target="_blank" rel="noopener noreferrer">
                    <RedditIcon className={`${css.socialIcon} ${css.redditSocialIcon}`} />
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div className={css.quickLinks}>
            <h3 className={css.sectionTitle}>Liens utiles</h3>
            <nav className={css.linkList}>
              <a href="/products" className={css.footerLink}>Tous les produits</a>
              <a href="/promotions" className={css.footerLink}>Bons Plans</a>
              <a href="/about" className={css.footerLink}>À propos</a>
              <a href="/contact" className={css.footerLink}>Contact</a>
              <a href="/devenir-vendeur" className={css.footerLink}>Devenir vendeur</a>
              <a href="/programme-affiliation" className={css.footerLink}>Programme d'affiliation</a>
              <a href="/blog" className={css.footerLink}>Notre blog</a>
            </nav>
          </div>

          {/* Tools */}
          <div className={css.customerSupport}>
            <h3 className={css.sectionTitle}>Nos outils</h3>
            <nav className={css.linkList}>
              <a href="/comparateur" className={css.footerLink}>Comparateur de produits</a>
              <a href="/accompagnement-projet-domotique" className={css.footerLink}>
                Accompagnement projet domotique
              </a>
              <a href="/packs" className={css.footerLink}>Nos packs</a>
              <a href="/programme-fidelite" className={css.footerLink}>Programme fidélité</a>
            </nav>
          </div>

          {/* Top Brands */}
          <div className={css.customerSupport}>
            <h3 className={css.sectionTitle}>Marques</h3>
            <nav className={css.linkList}>
              <a href="/brands" className={css.footerLink}>Toutes les marques</a>
              <a href="/products/somfy" className={css.footerLink}>Somfy</a>
              <a href="/products/aqara" className={css.footerLink}>Aqara</a>
              <a href="/products/delta-dore" className={css.footerLink}>Delta Dore</a>
              <a href="/products/bosch" className={css.footerLink}>Bosch</a>
              <a href="/products/reolink" className={css.footerLink}>Reolink</a>
            </nav>
          </div>

          {/* Top Categories */}
          <div className={css.customerSupport}>
            <h3 className={css.sectionTitle}>Catégories</h3>
            <nav className={css.linkList}>
              <a href="/categories" className={css.footerLink}>Toutes les catégories</a>
              <a href="/products/camera-securite" className={css.footerLink}>Caméras de sécurité</a>
              <a href="/products?pub_categoryLevel1=lawn-mowers" className={css.footerLink}>Robots tondeuses</a>
              <a href="/products?pub_categoryLevel1=smart-locks" className={css.footerLink}>Serrures connectées</a>
              <a href="/products?pub_categoryLevel1=smart-thermostats" className={css.footerLink}>Thermostats</a>
              <a href="/products?pub_categoryLevel1=lights" className={css.footerLink}>Éclairage</a>
            </nav>
          </div>

          {/* Connectivity */}
          <div className={css.customerSupport}>
            <h3 className={css.sectionTitle}>Connectivité</h3>
            <nav className={css.linkList}>
              <a href="/products?pub_connectivity=wifi" className={css.footerLink}>Wi-Fi</a>
              <a href="/products?pub_connectivity=zigbee" className={css.footerLink}>Zigbee</a>
              <a href="/products?pub_connectivity=zwave" className={css.footerLink}>Z-Wave</a>
              <a href="/products?pub_connectivity=matter" className={css.footerLink}>Matter</a>
              <a href="/products?pub_connectivity=bluetooth" className={css.footerLink}>Bluetooth</a>
            </nav>
          </div>

          {/* Compatibility */}
          <div className={css.customerSupport}>
            <h3 className={css.sectionTitle}>Compatibilité</h3>
            <nav className={css.linkList}>
              <a href="/compatibility" className={css.footerLink}>Toutes les compatibilités</a>
              <a href="/products?pub_compatibility=homeassistant" className={css.footerLink}>Home Assistant</a>
              <a href="/products?pub_compatibility=homekit" className={css.footerLink}>Apple HomeKit</a>
              <a href="/products?pub_compatibility=alexa" className={css.footerLink}>Amazon Alexa</a>
              <a href="/products?pub_compatibility=googlehome" className={css.footerLink}>Google Home</a>
            </nav>
          </div>

          {/* Newsletter signup */}
          <div className={css.newsletterSection}>
            <NewsletterSignup source="footer" className={css.footerNewsletter} />
          </div>

          {/* Copyright Section */}
          <div className={css.bottomContent}>
            <div className={css.bottomRight}>
              <p className={css.copyright}>
                © {new Date().getFullYear()} {'Neokasa'}. Tous droits réservés.
              </p>
              <div className={css.bottomLinks}>
                <a href="/terms-of-service" className={css.bottomLink}>Conditions d'utilisation</a>
                <a href="/privacy-policy" className={css.bottomLink}>Politique de confidentialité</a>
                <a href="/legal-notice" className={css.bottomLink}>Mentions légales</a>
                <button 
                  type="button"
                  onClick={handleOpenCookiePreferences} 
                  className={css.bottomLink}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                >
                  Gérer les cookies
                </button>
                <a href="/sitemap" className={css.bottomLink}>Plan du site</a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default FooterComponent;
